import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL, baseurl } from "../../_config";

const PlayersListByTeam = (props) => {
    const { players, matches, onTossReady, playingElevenMatch, fetchMatches, fetchPlayingElevenByMatch, fetchInnings, teamAPlayers, setTeamAPlayers, teamBPlayers, setTeamBPlayers } = props;

    const [selectedTeamA, setSelectedTeamA] = useState([]);
    const [selectedTeamB, setSelectedTeamB] = useState([]);

    const [teamASubmitted, setTeamASubmitted] = useState(false);
    const [teamBSubmitted, setTeamBSubmitted] = useState(false);

    // ✅ Separate existing Playing XI from backend
    useEffect(() => {
        if (!playingElevenMatch || !matches) return;

        const teamAExisting = playingElevenMatch.filter(
            (p) => Number(p.teamId) === Number(matches.teamA_id)
        );
        const teamBExisting = playingElevenMatch.filter(
            (p) => Number(p.teamId) === Number(matches.teamB_id)
        );

        // Remove duplicates (if backend returns them multiple times)
        const uniqueA = [...new Set(teamAExisting.map((p) => p.playerId))];
        const uniqueB = [...new Set(teamBExisting.map((p) => p.playerId))];

        if (uniqueA.length > 0) {
            setSelectedTeamA(uniqueA);
            setTeamASubmitted(true);
        }
        if (uniqueB.length > 0) {
            setSelectedTeamB(uniqueB);
            setTeamBSubmitted(true);
        }
    }, [playingElevenMatch, matches]);

    // ✅ Split players into Team A & Team B
    useEffect(() => {
        if (!players || !matches) return;

        const teamA = players
            .filter((p) => Number(p.teamId) === Number(matches.teamA_id))
            .map((p) => ({
                playerId: p.playersId,
                playerName: p.playerName,
                playerRole: p.playerRole,
                teamId: p.teamId,
                teamName: p.teamName || "Team A",
            }));

        const teamB = players
            .filter((p) => Number(p.teamId) === Number(matches.teamB_id))
            .map((p) => ({
                playerId: p.playersId,
                playerName: p.playerName,
                playerRole: p.playerRole,
                teamId: p.teamId,
                teamName: p.teamName || "Team B",
            }));

        setTeamAPlayers(teamA);
        setTeamBPlayers(teamB);
    }, [players, matches]);

    // ✅ Handle checkbox changes
    const handleCheckboxChange = (team, playerId) => {
        if (teamAPlayers.length < 11 || teamBPlayers.length < 11) {
            toast("Both teams must have at least 11 players to select Playing XI");
            return;
        }

        if (team === "A") {
            setSelectedTeamA((prev) => {
                if (prev.includes(playerId)) {
                    return prev.filter((id) => id !== playerId);
                } else if (prev.length < 11) {
                    return [...prev, playerId];
                } else {
                    toast.error("You can select only 11 players!");
                    return prev;
                }
            });
        } else {
            setSelectedTeamB((prev) => {
                if (prev.includes(playerId)) {
                    return prev.filter((id) => id !== playerId);
                } else if (prev.length < 11) {
                    return [...prev, playerId];
                } else {
                    toast.error("You can select only 11 players!");
                    return prev;
                }
            });
        }
    };

    // ✅ Submit Team XI
    const handleSubmitTeam = async (team) => {
        let selectedIds = [];
        let teamId;

        if (team === "A") {
            selectedIds = selectedTeamA;
            teamId = matches.teamA_id;
        } else {
            selectedIds = selectedTeamB;
            teamId = matches.teamB_id;
        }

        if (selectedIds.length !== 11) {
            toast.error("Please select exactly 11 players!");
            return;
        }

        const payload = {
            tournamentId: matches?.tournament_id,
            matchId: matches?.id,
            teamId,
            playerIds: selectedIds,
        };

        try {
            await axios.post(`${baseurl}${API_URL.addPlayingEleven}`, payload);
            toast.success(`✅ Team ${team === "A" ? "A" : "B"} Playing XI saved!`);

            if (team === "A") {
                setTeamASubmitted(true);
                localStorage.setItem("teamAplayingEleven", true);
                fetchPlayingElevenByMatch();
            } else {
                setTeamBSubmitted(true);
                localStorage.setItem("teamBplayingEleven", true);
                fetchMatches();
                fetchPlayingElevenByMatch();
                fetchInnings();
            }
        } catch (err) {
            toast.error("Error saving playing XI!");
        }
    };

    // const bothTeamsReady = teamASubmitted && teamBSubmitted;

    return (
        <div className="live-match-admin">
            <div className="live-header text-center">
                <h3>🏏 Playing XI Selection</h3>
                <p>Select exactly 11 players for each team. Toss unlocks after both teams are submitted.</p>
            </div>

            {/* {bothTeamsReady && (
                <div className="toss-section text-center my-3">
                    <h4>🪙 Both Teams Ready!</h4>
                    <button className="btn btn-primary" onClick={onTossReady}>
                        Proceed
                    </button>
                </div>
            )} */}

            <div className="team-selection d-flex justify-content-between flex-wrap gap-4">
                {/* 🏏 TEAM A */}
                <div className="team-list flex-fill p-3 border rounded bg-dark text-light">
                    <h5 className="mb-3">
                        {teamAPlayers[0]?.teamName || "Team A"}
                    </h5>
                    {teamAPlayers.length === 0 ? (
                        <p>No players found for this team.</p>
                    ) : (
                        <ul className="list-unstyled">
                            {teamAPlayers.map((item, i) => (
                                <li key={item.playerId} className="mb-2">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selectedTeamA.includes(item.playerId)}
                                            onChange={() =>
                                                handleCheckboxChange("A", item.playerId)
                                            }
                                            disabled={teamASubmitted}
                                        />{" "}
                                        {i + 1}. {item.playerName} ({item.playerRole})
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-white">
                        Selected: {selectedTeamA.length} / 11 players
                    </p>
                    <button
                        className="btn btn-success mt-3"
                        onClick={() => handleSubmitTeam("A")}
                        disabled={teamASubmitted}
                    >
                        {teamASubmitted ? "✅ Team A Submitted" : "Submit Team A XI"}
                    </button>
                </div>

                {/* 🏏 TEAM B */}
                <div className="team-list flex-fill p-3 border rounded bg-dark text-light">
                    <h5 className="mb-3">
                        {teamBPlayers[0]?.teamName || "Team B"} - Select Playing XI
                    </h5>
                    {teamBPlayers.length === 0 ? (
                        <p>No players found for this team.</p>
                    ) : (
                        <ul className="list-unstyled">
                            {teamBPlayers.map((item, i) => (
                                <li key={item.playerId} className="mb-2">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selectedTeamB.includes(item.playerId)}
                                            onChange={() =>
                                                handleCheckboxChange("B", item.playerId)
                                            }
                                            disabled={!teamASubmitted || teamBSubmitted}
                                        />{" "}
                                        {i + 1}. {item.playerName} ({item.playerRole})
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-white">
                        Selected: {selectedTeamB.length} / 11 players
                    </p>
                    <button
                        className="btn btn-success mt-3"
                        onClick={() => handleSubmitTeam("B")}
                        disabled={!teamASubmitted || teamBSubmitted}
                    >
                        {teamBSubmitted ? "✅ Team B Submitted" : "Submit Team B XI"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlayersListByTeam;
